import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import { useQueryClient } from '@tanstack/react-query';
import { notificationAlertActions } from 'app/pages/NotificationAlert/slice';
import {
  emailsGridActions,
  reducer as emailsGridReducer,
  sliceKey as emailsGridSliceKey,
} from 'app/pages/EmailsGridPage/slice';
import { uploadLeadsDetailsSaga } from 'app/pages/EmailsGridPage/saga';
import {
  selectGenerateInProgress,
  selectGenerateProgress,
  selectGenerateStartedDealerId,
  selectGenerateTemplates,
} from 'app/pages/EmailsGridPage/selectors';
import { useEmailsGridSocket } from 'app/pages/EmailsGridPage/hooks/useEmailsGridSocket';
import {
  clearGenerationState as clearLocalStorageGenerationState,
  loadGenerationState,
  saveGenerationState,
  updateGenerationProgress,
  updateSocketMessages,
} from 'app/pages/EmailsGridPage/utils/generationPersistence';
import type { SocketMessageItem } from 'app/pages/EmailsGridPage/hooks/useEmailsGridSocket';

export type UseEmailTemplateGenerationProgressHostOptions = {
  dealerId: string;
  enabled?: boolean;
  /** Called on socket template updates / completion (e.g. refresh grids). */
  onTemplateUpdate?: () => void;
  progressCardTitle?: string;
};

/**
 * Page-level host for email template generation progress (socket + Redux + localStorage).
 * Keeps progress visible when nested modals unmount (e.g. HIL resolve modal).
 * Same `EmailGenerationProgressCard` + socket flow as EmailsGridPage.
 */
export function useEmailTemplateGenerationProgressHost({
  dealerId,
  enabled = true,
  onTemplateUpdate,
  progressCardTitle = 'Generating email templates...',
}: UseEmailTemplateGenerationProgressHostOptions) {
  useInjectReducer({ key: emailsGridSliceKey, reducer: emailsGridReducer });
  useInjectSaga({ key: emailsGridSliceKey, saga: uploadLeadsDetailsSaga });

  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const generateTemplates = useSelector(selectGenerateTemplates);
  const generateInProgressFromRedux = useSelector(selectGenerateInProgress);
  const generateStartedDealerIdFromRedux = useSelector(selectGenerateStartedDealerId);
  const generateProgressFromRedux = useSelector(selectGenerateProgress);

  const [showGenerateProgressPopup, setShowGenerateProgressPopup] =
    React.useState(false);
  const [isGenerateInProgress, setIsGenerateInProgress] = React.useState(false);
  const [showSocketDetailsModal, setShowSocketDetailsModal] = React.useState(false);
  const [simulatedFilledCount, setSimulatedFilledCount] = React.useState(0);

  const refreshTemplates = React.useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: ['GET', 'api/v2/templates'],
      exact: false,
    });
    onTemplateUpdate?.();
  }, [onTemplateUpdate, queryClient]);

  const dispatchSetGenerateProgress = React.useCallback(
    (progress: { current: number; total: number }) => {
      dispatch(emailsGridActions.setGenerateProgress(progress));
      if (dealerId) {
        updateGenerationProgress(dealerId, progress);
      }
    },
    [dealerId, dispatch],
  );

  const persistSocketMessage = React.useCallback(
    (message: SocketMessageItem) => {
      if (dealerId) {
        updateSocketMessages(dealerId, message);
      }
    },
    [dealerId],
  );

  const {
    socketMessages,
    generationCompleted,
    completionResult,
    progressFromSocket,
    clearGenerationState,
  } = useEmailsGridSocket(
    enabled ? dealerId : '',
    refreshTemplates,
    () => dispatch(emailsGridActions.setGenerateCompleted()),
    dispatchSetGenerateProgress,
    persistSocketMessage,
    generateProgressFromRedux,
    [],
  );

  const totalSections = Math.max(progressFromSocket.total, 1);

  const streamingDataForCard = React.useMemo(
    () => ({
      message: {
        sections: Array.from({ length: totalSections }, (_, i) => ({
          index: i + 1,
          main: `Step ${i + 1}`,
          sub_text: '',
        })),
        ...(simulatedFilledCount >= 1 && {
          text: {
            key: { index: simulatedFilledCount },
            value: `Step ${simulatedFilledCount} of ${totalSections} completed.`,
          },
        }),
      },
    }),
    [simulatedFilledCount, totalSections],
  );

  const currentSectionForCard =
    showGenerateProgressPopup && totalSections >= 1
      ? {
          index: Math.min(simulatedFilledCount || 1, totalSections),
          type: 'step' as const,
          main: 'Generating...',
          sub_text: `Creating email templates (${simulatedFilledCount}/${totalSections})`,
        }
      : undefined;

  React.useEffect(() => {
    const current = progressFromSocket.current;
    if (current > simulatedFilledCount) {
      setSimulatedFilledCount(current);
    }
  }, [progressFromSocket.current, simulatedFilledCount]);

  React.useEffect(() => {
    if (!enabled || !dealerId) return;

    const persistedState = loadGenerationState(dealerId);
    if (persistedState?.generateInProgress) {
      setIsGenerateInProgress(true);
      setShowGenerateProgressPopup(true);
      if (persistedState.progress.current > 0) {
        setSimulatedFilledCount(persistedState.progress.current);
      }
      if (
        generateStartedDealerIdFromRedux !== dealerId ||
        !generateInProgressFromRedux
      ) {
        dispatch(
          emailsGridActions.requestGenerateEmailTemplates({ dealerId }),
        );
      }
      if (
        persistedState.progress.current !== generateProgressFromRedux.current ||
        persistedState.progress.total !== generateProgressFromRedux.total
      ) {
        dispatch(emailsGridActions.setGenerateProgress(persistedState.progress));
      }
    }
  }, [dealerId, enabled]);

  React.useEffect(() => {
    if (!enabled || !dealerId || generationCompleted) return;
    const active =
      generateInProgressFromRedux &&
      generateStartedDealerIdFromRedux === dealerId;
    if (active) {
      setIsGenerateInProgress(true);
      setShowGenerateProgressPopup(true);
    }
  }, [
    dealerId,
    enabled,
    generationCompleted,
    generateInProgressFromRedux,
    generateStartedDealerIdFromRedux,
  ]);

  React.useEffect(() => {
    if (!enabled) return;
    if (
      generateStartedDealerIdFromRedux != null &&
      generateStartedDealerIdFromRedux !== dealerId
    ) {
      setIsGenerateInProgress(false);
      setShowGenerateProgressPopup(false);
    }
  }, [dealerId, enabled, generateStartedDealerIdFromRedux]);

  React.useEffect(() => {
    if (!generationCompleted) return;
    setShowGenerateProgressPopup(false);
    setShowSocketDetailsModal(false);
    setIsGenerateInProgress(false);
    if (dealerId) {
      clearLocalStorageGenerationState(dealerId);
    }
    const generated = completionResult?.generated;
    const skipped = completionResult?.skipped;
    const parts: string[] = [];
    if (generated != null) parts.push(`${generated} generated`);
    if (skipped != null) parts.push(`${skipped} skipped`);
    dispatch(
      notificationAlertActions.addNotification({
        open: true,
        severity: 'success',
        message: parts.length
          ? `Template generation completed (${parts.join(', ')}).`
          : 'Template generation completed.',
      }),
    );
  }, [completionResult, dealerId, dispatch, generationCompleted]);

  React.useEffect(() => {
    if (generateTemplates?.error && isGenerateInProgress) {
      setIsGenerateInProgress(false);
      setShowGenerateProgressPopup(false);
    }
  }, [generateTemplates?.error, isGenerateInProgress]);

  const notifyGenerationStarted = React.useCallback(() => {
    setSimulatedFilledCount(0);
    setIsGenerateInProgress(true);
    setShowGenerateProgressPopup(true);
  }, []);

  return {
    progressCardTitle,
    showGenerateProgressPopup,
    isGenerateInProgress,
    showSocketDetailsModal,
    setShowSocketDetailsModal,
    streamingDataForCard,
    currentSectionForCard,
    socketMessages,
    generationCompleted,
    progressFromSocket,
    notifyGenerationStarted,
    clearGenerationState,
  };
}
